import React, { useState } from 'react';
import promocodeService from '../../utils/promocodeService';
import excelPromocodeUploader from '../../utils/excelPromocodeUploader';
import { supabase } from '../../utils/supabase';
import { PROMOCODE_CONFIG } from '../../config/promocode.js';

const PromocodeManager = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);


  const handleFileUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadFile) {
      setMessage('Please select a file to upload');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      // Parse Excel file first
      const excelData = await excelPromocodeUploader.parseExcelFile(uploadFile);
      
      // Use promocodeService which includes admin role check
      const result = await promocodeService.uploadPromocodesFromExcel(excelData);

      if (result.success) {
        setMessage(result.message);
        setMessageType('success');
        setUploadFile(null);
      } else {
        setMessage(result.error);
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Failed to upload promocodes');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    excelPromocodeUploader.generateExcelTemplate();
  };

  const downloadPromocodes = async () => {
    try {
      // Get all promocodes from database
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: promocodes, error } = await supabase
        .from(PROMOCODE_CONFIG.tableName)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        setMessage('Failed to fetch promocodes');
        setMessageType('error');
        return;
      }

      await excelPromocodeUploader.exportPromocodesToExcel(promocodes);
      setMessage('Promocodes downloaded successfully');
      setMessageType('success');
    } catch (error) {
      setMessage('Failed to download promocodes');
      setMessageType('error');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">
        Promocode Management
      </h3>

      {/* Upload Section */}
        <form onSubmit={handleFileUpload} className="space-y-4">
          <div>
            <label htmlFor="excelFile" className="block text-sm font-medium text-gray-700 mb-2">
              Excel File
            </label>
            <input
              type="file"
              id="excelFile"
              accept=".xlsx,.xls"
              onChange={(e) => setUploadFile(e.target.files[0])}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Upload Excel file with promocodes. Download template for correct format.
            </p>
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={loading || !uploadFile}
              className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Uploading...' : 'Upload Promocodes'}
            </button>

            <button
              type="button"
              onClick={downloadTemplate}
              className="bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Download Template
            </button>

            <button
              type="button"
              onClick={downloadPromocodes}
              className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              Download All Promocodes
            </button>
          </div>
        </form>

      {/* Message Display */}
      {message && (
        <div className={`mt-4 p-3 rounded-md ${
          messageType === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* Information */}
      <div className="mt-6 text-xs text-gray-500">
        <p>• <strong>Structured Format:</strong> Upload promocodes with Platform and Plan Type columns (required)</p>
        <p>• Structured promocodes must follow format: <strong>[Platform][Plan][Credits][Year][Random]</strong></p>
        <p>• All promocodes are validated before activation</p>
        <p>• Download template for correct Excel format</p>
      </div>
    </div>
  );
};

export default PromocodeManager;
